import type { FitAssessmentSuccessResponse } from '@shared/fit-analysis'

export interface FitAssessmentDesignSample {
  id: string
  label: string
  description: string
  payload: FitAssessmentSuccessResponse
}

export const fitAssessmentDesignSamples: FitAssessmentDesignSample[] = [
  {
    id: 'pastry-chef-no-fit',
    label: 'Hard No-Fit',
    description: 'Clear domain mismatch with no relevant role overlap.',
    payload: {
      success: true,
      data: {
        verdict: 'Not a Fit',
        fitScore: 0,
        confidence: 'High',
        summary: 'No relevant experience or skills in pastry chef role',
        matches: [],
        keyGaps: [
          'No documented evidence of pastry chef experience',
          'No skills or training in pastry arts',
        ],
        disqualifiers: ['Lack of relevant experience or skills for a pastry chef role'],
        transferableStrengths: [],
        recommendation:
          "Not a good fit for this role. Consider a different role that aligns with Eric's skills and experience in mechatronic systems engineering.",
      },
    },
  },
  {
    id: 'robotics-controls-partial',
    label: 'Stretch Partial',
    description: 'Relevant hardware overlap with missing robotics stack depth.',
    payload: {
      success: true,
      data: {
        verdict: 'Partial Fit',
        fitScore: 58,
        confidence: 'Medium',
        summary:
          'Relevant electromechanical build experience is present, but the role expects a deeper production robotics software stack than the resume documents.',
        matches: [
          "Hands-on mechatronics and electromechanical integration projects align with the role's hardware-heavy scope.",
          'Documented PCB design, testing, and controls work supports debugging and prototype bring-up responsibilities.',
          'Rapid prototyping experience suggests strong execution in build-and-iterate environments.',
        ],
        keyGaps: [
          'No explicit ROS or deployed robotics middleware experience is documented.',
          'The resume does not show production-grade C++ software ownership on a robotics platform.',
          'No direct evidence of long-horizon autonomous system validation workflows.',
        ],
        disqualifiers: [],
        transferableStrengths: [
          'Strong hands-on systems debugging should transfer well to lab validation work.',
          'Cross-disciplinary hardware and controls experience reduces onboarding risk for electro-mechanical integration tasks.',
        ],
        recommendation:
          'Worth a recruiter screen if the team can tolerate ramp-up on the robotics software stack, but this is not yet a clean match for a software-heavy controls role.',
      },
    },
  },
  {
    id: 'embedded-intern-strong',
    label: 'Strong Fit',
    description: 'Direct overlap with embedded, test, and systems integration work.',
    payload: {
      success: true,
      data: {
        verdict: 'Strong Fit',
        fitScore: 82,
        confidence: 'High',
        summary:
          'The documented resume evidence aligns well with an embedded or electromechanical integration internship focused on hardware bring-up, testing, and prototype development.',
        matches: [
          'Direct PCB design and testing work maps to embedded hardware bring-up responsibilities.',
          'Hands-on controls and Arduino-based prototyping align with entry-level firmware-adjacent lab work.',
          'High-voltage safety and battery integration experience signals comfort with physical systems and test discipline.',
        ],
        keyGaps: [
          'The resume should still validate depth in version-controlled firmware development during interviews.',
        ],
        disqualifiers: [],
        transferableStrengths: [
          'Strong prototype execution and cross-functional communication should translate well to internship environments.',
          'Practical troubleshooting experience supports rapid iteration on lab hardware.',
        ],
        recommendation:
          'Good candidate for interview progression, especially for teams hiring interns who need hands-on hardware execution more than deep production firmware specialization.',
      },
    },
  },
]

export const defaultFitAssessmentDesignSample = fitAssessmentDesignSamples[0]
